/**
 * kbApi.ts — TanStack Query hooks for the Knowledge Base backend
 * (wiki-llm-service, B1a — live). This is the first surface wired to a real
 * backend (B1b Step 1). Today it covers the document LIST read; upload + review
 * decisions are the next slice and still run through the mock kbStore.
 *
 * Maps the backend wire shapes (api/schemas.py) onto the frontend KbDoc model
 * (mock.ts), absorbing two convention mismatches found during integration:
 *   - the backend lowercases the `domain` value; the UI's domain config keys
 *     are mixed-case ("HR", "Procurement"), so we resolve case-insensitively.
 *   - status uses an underscore (`pending_review`) vs the UI's hyphen
 *     (`pending-review`).
 * Known gap: the list endpoint's DocumentSummary carries NO chunk count, so
 * `chunks` is 0 until the backend adds `chunk_count` to DocumentSummary (a B1a
 * follow-up tracked in Frontend_action_contract.md / B1b_backend_build_plan.md).
 */
import { useAuth0 } from "@auth0/auth0-react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { ApiError, apiErrorMessage, useApi } from "./api";
import {
  MOCK_KB_DOMAINS,
  type KbDecision,
  type KbDoc,
  type KbScanOutcome,
  type KbDocStatus,
  type KbLintFinding,
  type KbReview,
  type KbWikiEdge,
  type KbWikiPage,
  type LintCategory,
  type WikiPageType,
} from "./mock";

/** Wire shape of DocumentSummary (api/schemas.py). */
interface DocumentSummaryDto {
  id: string;
  title: string;
  original_filename: string;
  domain: string | null;
  status: string;
  uploaded_by_sub: string;
  created_at: string;
  updated_at: string;
  /** Not yet returned by the list endpoint — see "Known gap" above. */
  chunk_count?: number;
  /** Live synthesis stage while status === "ingesting" (drives the progress UI). */
  synthesis_stage?: string | null;
  /** `D-59` — which progress ladder this document is on: `"full"` (seven
   *  pipeline stages) or `"light"` (four). Absent on a service build that
   *  predates light-only preparation, and absence reads as `"full"`, which is
   *  what such a build actually does. */
  ingestion_mode?: string | null;
  /** The host application's project, as a snapshot from upload time. */
  project_ref_id?: string | null;
  project_ref_name?: string | null;
  project_ref_synced_at?: string | null;
  /** `KBR-24`. Absent on an older service build — see `KbDoc.canRecheck`. */
  recheck_available?: boolean;
  recheck_blocked_reason?: string | null;
  recheck_blocked_code?: string | null;
  last_error?: string | null;
  /** `D-53` — the second of the three distinct conditions. Absent on an older
   *  service build; silence maps to "none". */
  synthesis_state?: string;
  /** `KBR-62`. Absent on a service build that predates the disclosure, which is
   *  why `toKbDoc` maps a missing object to `unknown` rather than to
   *  `complete`: silence is not a clean bill. */
  scan?: {
    state?: string;
    cause?: string | null;
    comparisons?: number | null;
    comparison_limit?: number | null;
    documents_compared?: number | null;
    documents_uncompared?: number | null;
    chunks?: number | null;
  } | null;
}

interface DocumentListDto {
  documents: DocumentSummaryDto[];
  total: number;
  limit: number;
  offset: number;
}

const STATUS_MAP: Record<string, KbDocStatus> = {
  ingesting: "ingesting",
  live: "live",
  pending_review: "pending-review",
  rejected: "rejected",
  archived: "archived",
};

function resolveDomainKey(raw: string | null): string {
  if (!raw) return "Altro";
  const hit = MOCK_KB_DOMAINS.find((d) => d.key.toLowerCase() === raw.toLowerCase());
  return hit ? hit.key : raw;
}

/**
 * The Italian sentence for a re-check refusal, chosen by the server's stable
 * CODE — never by re-deriving the rule, and never by printing the server's own
 * `message`.
 *
 * `message` is English prose that opens with a document UUID, written for a
 * developer reading a 409: «document 3f2b1c9e-… is 'live', not 'rejected'. A
 * re-check recovers a document whose validation failed…». It was being shown
 * verbatim as the tooltip of an Italian button, which an independent review
 * flagged as the primary user-visible surface of this feature being in the wrong
 * language.
 *
 * The split is the same one an HTTP status line makes: the server decides WHAT
 * happened and owns the rule; the interface decides HOW TO SAY IT. An unknown
 * code falls back to a generic Italian sentence rather than leaking the English,
 * so a newer server can add a refusal without this file printing developer prose
 * at a customer.
 */
const RECHECK_REASON_IT: Record<string, string> = {
  not_found: "Documento non trovato.",
  wrong_status:
    "Disponibile solo per un documento la cui validazione non è riuscita. " +
    "Se è ancora in elaborazione, attendi che finisca.",
  human_decided:
    "Su questo documento è già stata presa una decisione da una persona. " +
    "Un nuovo controllo non può riaprirla.",
  run_in_flight: "C'è già un'elaborazione in corso su questo documento. Attendi che finisca.",
  no_chunks:
    "L'elaborazione si è interrotta prima di salvare il contenuto: non c'è nulla da " +
    "controllare. Elimina il documento e ricaricalo.",
  never_validated:
    "Questo documento non è mai arrivato al controllo delle contraddizioni e le sue " +
    "pagine non sono state create. Elimina il documento e ricaricalo.",
  forbidden: "Riservato agli amministratori del workspace.",
  // `D-53` polish (tester Finding 2): the member-403 shape now carries its own
  // code, so the sentence can say who CAN act instead of a generic refusal.
  admin_only: "Solo un amministratore può avviare un nuovo controllo.",
};

/**
 * The Italian sentence for a re-check REFUSAL THAT CAME BACK FROM A CLICK.
 *
 * Added after an independent tester measured what the product actually said. The
 * server was changed so a 409 carries `{code, message}` instead of a bare string
 * — *precisely so the client could say it in Italian* — and then nothing on this
 * side was taught to read it. `api.ts`'s generic `describeDetail` understands a
 * string and FastAPI's 422 array, so an object fell through to its catch-all and
 * every refusal became the same sentence:
 *
 *     «Controllo non riuscito: Richiesta non valida.»
 *
 * "Someone has already decided this", "a run is in flight" and "there is nothing
 * to check" were indistinguishable — and the OLD string-shaped payload, which at
 * least showed the English, was better. A payload improved for a consumer that
 * was never written is not an improvement; it is a regression with a good
 * intention. This is the consumer.
 *
 * The KB-specific knowledge lives here rather than in `api.ts`, which is generic
 * HTTP plumbing and must not learn what a re-check is.
 */
export function recheckErrorIt(err: unknown): string {
  const detail =
    err instanceof ApiError && typeof err.body === "object" && err.body !== null
      ? (err.body as { detail?: unknown }).detail
      : undefined;
  if (typeof detail === "object" && detail !== null && "code" in detail) {
    const it = recheckReasonIt((detail as { code?: string }).code ?? null, null);
    if (it) return it;
  }
  return apiErrorMessage(err);
}

export function recheckReasonIt(
  code: string | null | undefined,
  fallbackEnglish: string | null | undefined,
): string | null {
  if (!code && !fallbackEnglish) return null;
  if (code && RECHECK_REASON_IT[code]) return RECHECK_REASON_IT[code];
  return "Controllo non disponibile per questo documento.";
}

/**
 * `KBR-62` — the wire object, normalised.
 *
 * A missing object, an unrecognised state and an older service build all map to
 * `unknown`, never to `complete`. The whole feature exists because "we know the
 * check finished" and "we have no record" were indistinguishable; defaulting
 * silence to the reassuring answer would rebuild that in the client.
 */
/**
 * EXPORTED for its test, and the reason is a measurement rather than a
 * preference. A mutation that made a missing `scan` object read as
 * `complete` instead of `unknown` left the whole suite GREEN: every
 * rendering assertion goes through `ScanIncompleteNotice`, which shows
 * nothing for either state, so no screen can tell them apart. That is
 * correct for the screen and useless as a control — and the difference is
 * exactly the defect this feature exists to prevent, because a service
 * build that predates the disclosure would then report every document as
 * fully checked.
 */
export function toScanOutcome(dto: DocumentSummaryDto["scan"]): KbScanOutcome {
  const state = dto?.state;
  return {
    state: state === "complete" || state === "incomplete" ? state : "unknown",
    // `HON-6`: an unrecognised or absent cause maps to null, not to "budget" —
    // the same reasoning as `state`'s "unknown" fallback. An older service
    // build sends no cause, and guessing the reassuring one is how a screen
    // states something nobody measured.
    cause: dto?.cause === "budget" || dto?.cause === "failures" ? dto.cause : null,
    comparisons: dto?.comparisons ?? null,
    comparisonLimit: dto?.comparison_limit ?? null,
    documentsCompared: dto?.documents_compared ?? null,
    documentsUncompared: dto?.documents_uncompared ?? null,
    chunks: dto?.chunks ?? null,
  };
}

function toKbDoc(dto: DocumentSummaryDto): KbDoc {
  return {
    id: dto.id,
    filename: dto.original_filename,
    domain: resolveDomainKey(dto.domain),
    uploader: dto.uploaded_by_sub,
    date: dto.created_at.slice(0, 10),
    chunks: dto.chunk_count ?? 0,
    status: STATUS_MAP[dto.status] ?? "ingesting",
    synthesisStage: dto.synthesis_stage ?? null,
    ingestionMode: dto.ingestion_mode === "light" ? "light" : "full",
    projectId: dto.project_ref_id ?? null,
    projectName: dto.project_ref_name ?? null,
    projectSyncedAt: dto.project_ref_synced_at ?? null,
    // `?? false`, not `?? true`: a service build that predates `KBR-24` sends
    // neither field, and the safe reading of silence is "not offered".
    canRecheck: dto.recheck_available ?? false,
    recheckBlockedReason: recheckReasonIt(dto.recheck_blocked_code, dto.recheck_blocked_reason),
    lastError: dto.last_error ?? null,
    scan: toScanOutcome(dto.scan),
    synthesisState: dto.synthesis_state ?? "none",
  };
}

/** Live document list for the active tenant (RLS-scoped server-side). Polls
 * every 2.5s WHILE any doc is still ingesting, so the synthesis progress (and
 * the doc flipping to "live") updates on its own — no manual refresh. */
export function useKbDocuments(): UseQueryResult<KbDoc[]> {
  const api = useApi();
  return useQuery<KbDoc[]>({
    queryKey: ["kb", "documents"],
    refetchInterval: (query) =>
      (query.state.data ?? []).some((d) => d.status === "ingesting") ? 2500 : false,
    queryFn: async ({ signal }): Promise<KbDoc[]> => {
      const res = await api.wikiLLM.get<DocumentListDto>("/v1/documents", { signal });
      return res.documents.map(toKbDoc);
    },
  });
}

/**
 * The same list, narrowed server-side to ONE host-application project.
 *
 * Server-side and not `.filter()` on the client: `GET /v1/documents` caps at
 * 100 rows per page, so client-side filtering is correct for a small library
 * and silently wrong for a large one — it would quietly stop seeing a
 * project's documents once an organization crossed the page
 * (`feedback_partial_reference_data_is_invisible`). The `project_id` filter was
 * added to the KB service for exactly this call.
 *
 * Polls on the same rule as the unscoped list: every 2.5s while anything is
 * still ingesting, then stops.
 */
export function useKbProjectDocuments(projectId: string | null): UseQueryResult<KbDoc[]> {
  const api = useApi();
  return useQuery<KbDoc[]>({
    // The project is part of the key, so two projects never share a cache
    // entry — the bug where switching project shows the previous one's
    // progress for one frame.
    queryKey: ["kb", "documents", "project", projectId],
    enabled: projectId != null,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((d) => d.status === "ingesting") ? 2500 : false,
    queryFn: async ({ signal }): Promise<KbDoc[]> => {
      /* Walk pages until the server's own `total` is exhausted.
         `limit` is unspecified by default, which the server reads as **50**
         (`documents.py:297`, capped at 100). An independent designer found the
         consequence and it is the worst shape a bug can have: for any project
         whose KB documents are older than ~50 other org-wide uploads, the first
         page contains NONE of them, the filter below removes what is left, and
         the lane reads a stable, non-alarming, permanently wrong number with no
         error anywhere. Walking to `total` closes that — and it also makes the
         client filter below COMPLETE rather than merely narrowing, because a
         full walk sees every row even when the server ignored `project_id`. */
      const PAGE = 100; // the server's own maximum
      const MAX_PAGES = 20; // 2000 documents; a bound, not a limit anyone should hit
      const raw: DocumentSummaryDto[] = [];
      let total = Infinity;
      let pages = 0;
      while (raw.length < total && pages < MAX_PAGES) {
        const res = await api.wikiLLM.get<DocumentListDto>("/v1/documents", {
          signal,
          params: { project_id: projectId as string, limit: PAGE, offset: raw.length },
        });
        total = res.total;
        if (res.documents.length === 0) break; // defensive: never loop on an empty page
        raw.push(...res.documents);
        pages += 1;
      }
      /* If the walk was truncated we cannot claim completeness, and a percentage
         computed over an unknown fraction is worse than none. Throwing puts the
         query in `error`, so the lane shows nothing rather than a wrong number —
         LOUD beats plausible. */
      if (raw.length < total) {
        throw new Error(
          `KB document walk truncated at ${raw.length} of ${total} for project ${projectId}`,
        );
      }
      const res = { documents: raw } as DocumentListDto;
      /* Re-filter on the client as well, and it is NOT redundant.
         `project_id` is new on the KB service. FastAPI IGNORES an unknown query
         parameter — measured, not assumed: a request carrying a parameter the
         server does not define came back with the WHOLE library and a 200. So a
         frontend deployed ahead of the KB service would compute this tender's
         progress over every tender in the organization, silently and with no
         error anywhere.
         This does not make the server-side filter optional: the list caps at
         100 rows, so only the server can be correct at scale. The two answer
         different failures — the server one for size, this one for version
         skew. Deploy order is still KB service first. */
      return res.documents.map(toKbDoc).filter((d) => d.projectId === projectId);
    },
  });
}

/** Wire shape of DocumentCreated (POST /v1/documents response). */
interface DocumentCreatedDto {
  id: string;
  status: string;
  title: string;
  original_filename: string;
  domain: string | null;
  created_at: string;
}

const BACKEND_DOMAINS = new Set([
  "hr",
  "it",
  "procurement",
  "product",
  "reference",
  "other",
  "project",
]);

/**
 * Map a frontend domain key (mixed-case, a superset — e.g. "Altro", "Project")
 * to a backend-accepted domain. The backend only allows the six in
 * BACKEND_DOMAINS (api/documents.py); anything else folds into "other".
 */
function toBackendDomain(key: string | undefined): string | undefined {
  if (!key) return undefined;
  const k = key.trim().toLowerCase();
  return BACKEND_DOMAINS.has(k) ? k : "other";
}

export interface KbUploadInput {
  file: File;
  title?: string;
  domain?: string;
  /**
   * The tender project this document came from. The KB stores it as a SNAPSHOT,
   * not a link: the two services use separate databases, so there is no foreign
   * key and the name can go stale if the project is renamed. Both fields travel
   * together or neither does — the backend refuses a half-link with a 422.
   */
  projectId?: string;
  projectName?: string;
}

/** Upload a document to the KB (multipart → POST /v1/documents). Refetches the list on success. */
export function useUploadKbDocument(): UseMutationResult<DocumentCreatedDto, Error, KbUploadInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      title,
      domain,
      projectId,
      projectName,
    }: KbUploadInput): Promise<DocumentCreatedDto> => {
      const form = new FormData();
      form.append("file", file);
      if (title) form.append("title", title);
      const backendDomain = toBackendDomain(domain);
      if (backendDomain) form.append("domain", backendDomain);
      // Only when BOTH are present. Sending one alone is a 422 by design, and
      // guarding here keeps a caller that has an id but no name yet from failing
      // the whole upload over the label.
      if (projectId && projectName) {
        form.append("project_id", projectId);
        form.append("project_name", projectName);
      }
      return api.wikiLLM.post<DocumentCreatedDto>("/v1/documents", { formData: form });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kb", "documents"] });
    },
  });
}

/**
 * `KBR-24` / `D-36` — «Controlla di nuovo»: repeat ONLY the contradiction check
 * on a document whose validation failed for a technical reason.
 *
 * `POST /v1/documents/{id}/recheck` → 202 and the document goes back to
 * `ingesting`, so it stays invisible to readers and to the assistant until the
 * check finishes. Admin-only on the server; the button is also gated on the
 * client, and the SERVER is the gate that counts.
 *
 * Every refusal is a 409 whose `detail` is the server's own sentence. It is
 * shown to the person verbatim rather than mapped to a message of ours: the
 * refusals name a state ("already has 1 run(s) in flight", "carries 2 review
 * decision(s) made by a person"), and a re-worded copy is one more thing to keep
 * in agreement with a rule that lives in Python.
 */
export interface RecheckStartedDto {
  id: string;
  run_id: string;
  status: string;
}

export function useRecheckKbDocument(): UseMutationResult<RecheckStartedDto, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<RecheckStartedDto> =>
      api.wikiLLM.post<RecheckStartedDto>(`/v1/documents/${id}/recheck`),
    onSuccess: () => {
      // Invalidate rather than write the response into the cache: the list is
      // the thing that decides whether the button is offered next, and only the
      // server can answer that. Refetching also restarts the 2.5s progress poll,
      // because the document is now `ingesting` again.
      void qc.invalidateQueries({ queryKey: ["kb", "documents"] });
    },
  });
}

/**
 * `D-40` — what deleting this PDF would destroy, asked BEFORE anything is
 * deleted so the confirmation dialog can list it.
 *
 * The server computes this with the same function that then carries the
 * deletion out, which is the point: a second implementation on this side would
 * be a prediction to keep in agreement with a rule that lives in Python, and
 * this project has already paid for that shape more than once. Nothing here
 * decides what is affected — it renders what the server says is.
 *
 * `enabled` gates it on the dialog being open. It is a genuinely more expensive
 * question than a row of the list (pages, page-source links, audit rows, review
 * rows), and charging every visit to the Knowledge Base for an answer almost
 * nobody asks for would be paying for the confirmation of a deletion that has
 * not been requested.
 */
export interface KbDeletionAffectedPageDto {
  id: string;
  title: string;
  type: string;
  has_manual_contributions: boolean;
  also_from: string[];
}

export interface KbDeletionPreviewDto {
  document_id: string;
  document_title: string;
  original_filename: string;
  pages: KbDeletionAffectedPageDto[];
  pending_reviews: number;
  decided_reviews: number;
  stranded_documents: number;
  stranded_recoverable: number;
  stranded_blocked: number;
  run_in_flight: boolean;
}

export function useKbDeletionPreview(
  id: string | null,
): UseQueryResult<KbDeletionPreviewDto, Error> {
  const api = useApi();
  return useQuery({
    queryKey: ["kb", "documents", id, "deletion-preview"],
    enabled: id !== null,
    // Never served from cache: between opening the dialog once and opening it
    // again a colleague may have edited a summary or a run may have written new
    // ones, and a stale list understates what the confirm destroys.
    staleTime: 0,
    gcTime: 0,
    queryFn: (): Promise<KbDeletionPreviewDto> =>
      api.wikiLLM.get<KbDeletionPreviewDto>(`/v1/documents/${id!}/deletion-preview`),
  });
}

/**
 * `D-40` — delete a KB document AND every summary written from it.
 *
 * This used to be a soft delete (status → `archived`). Omar, 2026-09-06:
 * *"Confirming must perform the stated deletion: hiding pages while their
 * content remains searchable or accessible elsewhere is insufficient."* It is
 * now irreversible, which is why nothing calls it without the confirmation
 * dialog in front of it.
 */
export function useDeleteKbDocument(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<void> => api.wikiLLM.del<void>(`/v1/documents/${id}`),
    onSuccess: () => {
      // The documents list, and every surface that shows what the deleted
      // summaries were on: the graph loses nodes, the pages lose bodies, the
      // review queue loses rows. Invalidating only the list would leave a
      // deleted page drawn in Esplora until something else refetched it.
      //
      // The dialog's own preview is excluded, and measured rather than assumed:
      // without the predicate the invalidation reaches the still-mounted dialog
      // one render before it closes, and the browser asks the server what
      // deleting the document would destroy — for a document that has just been
      // destroyed. A 404 in the network log after every successful deletion,
      // with nothing on screen to explain it.
      void qc.invalidateQueries({
        queryKey: ["kb"],
        predicate: (q) => q.queryKey[3] !== "deletion-preview",
      });
    },
  });
}

// ── Graph / chat / reviews / lint (B1b synthesis surfaces) ──────────────────

const ROLE_CLAIM = "https://cme-studio.app/role";

/** Roles from the custom claim. The Auth0 Login Action emits a single string
 * today, but its behaviour with two roles assigned is unverified — so tolerate an
 * array rather than silently dropping one. */
function claimRoles(claim: unknown): string[] {
  if (typeof claim === "string") return [claim];
  if (Array.isArray(claim)) return claim.filter((r): r is string => typeof r === "string");
  return [];
}

/** ACC-11 — the browser's mirror of `capabilities.ROLE_CAPABILITIES`.
 *
 * This is a COPY of a table that lives in Python, which is a thing worth being
 * uneasy about: the two can drift, and when they do the symptom is a control the
 * user can see and the API refuses (or worse, one hidden from someone allowed to
 * use it). It is duplicated anyway because the alternative — asking the server what
 * the caller may do — is a round trip on every render, and because the server
 * remains the only real gate. **The rule is: this table may only ever be a subset
 * of the server's.** Hiding a button somebody is allowed to press is a UI bug;
 * showing one the server will refuse is a broken product. When roles change, change
 * `tender-api/src/tender_api/capabilities.py` first. */
const CAPABILITIES: Record<string, readonly string[]> = {
  reviewer: [],
  // `member` is what every ordinary user of the live product holds today — the
  // Auth0 Login Action emits one of super_admin/admin/member. It deliberately does
  // NOT carry kb:approve, which is admin-only in the shipped product.
  member: [
    "project:write",
    "document:write",
    "analysis:run",
    "analysis:edit",
    "supplier:write",
    "assistant:use",
  ],
  editor: [
    "project:write",
    "document:write",
    "analysis:run",
    "analysis:edit",
    "supplier:write",
    "assistant:use",
    "kb:approve",
  ],
  admin: [
    "project:write",
    "document:write",
    "analysis:run",
    "analysis:edit",
    "supplier:write",
    "assistant:use",
    "kb:approve",
    // `D-41`, Omar 2026-09-06. The first capability an Admin holds and an Editor
    // does not — permanent deletion of a source PDF and every summary made from it.
    "document:delete",
  ],
  super_admin: [
    "project:write",
    "document:write",
    "analysis:run",
    "analysis:edit",
    "supplier:write",
    "assistant:use",
    "kb:approve",
    "platform:manage",
    "document:delete",
  ],
};

/** Exported for `capabilityMirror.test.ts` only, which asserts against
 *  `tender_api/capabilities.py` that this table never claims MORE than the
 *  server's. The rule was stated in three comments and enforced nowhere. */
export const CAPABILITIES_FOR_TEST: Record<string, readonly string[]> = CAPABILITIES;

/** Does the signed-in user hold this capability? Union across roles, exactly as
 * the server does — intersection would mean adding a role could take powers away. */
export function useCapability(capability: string): boolean {
  const { user } = useAuth0();
  return claimRoles(user?.[ROLE_CLAIM]).some((r) => CAPABILITIES[r]?.includes(capability));
}

/** May the signed-in user decide what enters the Knowledge Base?
 *
 * Omar, 2026-08-19: Admin AND Editor. Backed by `kb:approve` on
 * `POST /v1/projects/{id}/documents/kb-forward`, so the button and the endpoint
 * agree by construction rather than by two people remembering the same rule. */
export function useCanApproveKb(): boolean {
  return useCapability("kb:approve");
}

/** May the signed-in user permanently delete a source PDF from «Documenti del
 * progetto»?
 *
 * `D-41`, Omar 2026-09-06 — administrators only. Backed by `document:delete` on
 * `DELETE /v1/documents/{document_id}`, so the control and the endpoint agree by
 * construction; `capabilityMirror.test.ts` pins this table against
 * `tender_api/capabilities.py` so it cannot drift.
 *
 * Deliberately a CAPABILITY here and a ROLE (`useIsKbAdmin`) on the Knowledge
 * Base side, because the two backends authorise differently: `tender-api` runs
 * the `ACC-11` capability table, `wiki-llm-service` still runs `require_admin`.
 * Asking each the question it can actually answer is what keeps a control from
 * claiming a gate that does not exist. Today both resolve to admin+super_admin. */
export function useCanDeleteDocument(): boolean {
  return useCapability("document:delete");
}

/** Reviews + lint are admin-only; gate the calls so non-admins don't 403-loop.
 * A platform `super_admin` counts as an admin here — otherwise granting that role
 * would REVOKE the review queue and lint from the person who runs the platform,
 * which is exactly backwards. Mirrors `CurrentUser.is_admin` in both backends.
 *
 * NOTE: this is still ROLE-based on purpose. The review queue and lint live in
 * `wiki-llm-service`, which `ACC-11` has not touched — its guards are still
 * `require_admin`. Renaming this to a capability would claim a gate that does not
 * exist yet. The KB *approval* control on `DocumentsPage` is the one that moved,
 * and it now asks `useCapability("kb:approve")`. */
export function useIsKbAdmin(): boolean {
  const { user } = useAuth0();
  const roles = claimRoles(user?.[ROLE_CLAIM]);
  return roles.includes("admin") || roles.includes("super_admin");
}

/** True only for the platform operator — gates the Super Admin panel. */
export function useIsSuperAdmin(): boolean {
  const { user } = useAuth0();
  return claimRoles(user?.[ROLE_CLAIM]).includes("super_admin");
}

/** Don't retry admin-only 401/403s (the global retry:1 would otherwise re-hit). */
function adminRetry(count: number, err: Error): boolean {
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) return false;
  return count < 1;
}

// --- graph ---
interface GraphNodeDto {
  id: string;
  title: string;
  type: WikiPageType;
  domain: string | null;
  chunks: number;
  sourceFileIds: string[];
  pageRefs: string[];
  status: string;
}
interface GraphDto {
  nodes: GraphNodeDto[];
  edges: { from: string; to: string; type: "ref" | "shared_source" | "shared_concept" }[];
}

export interface KbGraph {
  pages: KbWikiPage[];
  edges: KbWikiEdge[];
}

/** `pollWhileBusy` polls the graph every 3s (used while a doc is synthesizing,
 * so freshly-written pages appear in Esplora without a manual reload). */
export function useKbGraph(opts?: { pollWhileBusy?: boolean }): UseQueryResult<KbGraph> {
  const api = useApi();
  return useQuery({
    queryKey: ["kb", "graph"],
    refetchInterval: opts?.pollWhileBusy ? 3000 : false,
    queryFn: async ({ signal }): Promise<KbGraph> => {
      const res = await api.wikiLLM.get<GraphDto>("/v1/graph", { signal });
      return {
        pages: res.nodes.map((n) => ({
          id: n.id,
          title: n.title,
          type: n.type,
          domain: resolveDomainKey(n.domain),
          chunks: n.chunks,
          sourceFileIds: n.sourceFileIds,
          pageRefs: n.pageRefs,
          status: STATUS_MAP[n.status] ?? "live",
        })),
        edges: res.edges.map((e) => ({ from: e.from, to: e.to, type: e.type })),
      };
    },
  });
}

// --- page detail (reading view) ---
export interface KbPageSource {
  documentId: string;
  filename: string;
  /** `null` when this reader may not open the source document (`SEC-1`). The
   *  source is still named; only the download link is withheld. */
  url: string | null;
}
export interface KbPageDetail {
  id: string;
  title: string;
  type: WikiPageType;
  domain: string;
  status: KbDocStatus;
  properties: Record<string, unknown>;
  bodyMarkdown: string;
  sources: KbPageSource[];
}
interface WikiPageDetailDto {
  id: string;
  title: string;
  type: WikiPageType;
  domain: string | null;
  status: string;
  properties: Record<string, unknown>;
  body_markdown: string;
  sources: { document_id: string; filename: string; url: string | null }[];
}

/** Fetch one synthesized page's display properties + rendered Markdown body. */
export function useKbPage(pageId: string | null): UseQueryResult<KbPageDetail> {
  const api = useApi();
  return useQuery({
    queryKey: ["kb", "page", pageId],
    enabled: !!pageId,
    queryFn: async ({ signal }): Promise<KbPageDetail> => {
      const dto = await api.wikiLLM.get<WikiPageDetailDto>(`/v1/pages/${pageId}`, { signal });
      return {
        id: dto.id,
        title: dto.title,
        type: dto.type,
        domain: resolveDomainKey(dto.domain),
        status: STATUS_MAP[dto.status] ?? "live",
        properties: dto.properties,
        bodyMarkdown: dto.body_markdown,
        sources: dto.sources.map((s) => ({
          documentId: s.document_id,
          filename: s.filename,
          url: s.url,
        })),
      };
    },
  });
}

// --- chat / query ---
export interface KbQueryResult {
  answer: string;
  /** `documentTitle`/`pageStart`/`pageEnd` — `KBR-20`: for a citation whose
   *  evidence came from an original PDF chunk, the source document and the
   *  exact page range, so the reader can go from the answer to the page.
   *  Absent for synthesis-backed citations and on older servers. */
  citations: {
    /** `KBR-68`: null when the evidence came from a document with no
     *  synthesized page — `D-53` publishes a document too large to synthesize,
     *  and before this such a document was quoted in answers and cited in
     *  none. Exactly one of `pageId` / `documentId` may be null; the server
     *  refuses a citation naming neither. */
    pageId?: string | null;
    /** `KBR-68`: the SOURCE document behind this citation. Present for every
     *  document- or claim-backed citation; null only for a synthesized page
     *  cited in its own right. What `GET /v1/documents/{id}/source-url` opens. */
    documentId?: string | null;
    title: string;
    snippet: string;
    score: number;
    documentTitle?: string | null;
    pageStart?: number | null;
    pageEnd?: number | null;
  }[];
}
export interface KbQueryInput {
  question: string;
  domain?: string;
  topK?: number;
  /** KB-25 / D-33: which project the question is being asked INSIDE. Omitted
   *  means "outside a project", which the server reads as shared organisation
   *  knowledge only — not as a missing value to be guessed at. */
  projectId?: string | null;
}

export interface KbDocumentSource {
  url: string;
  filename: string;
  documentTitle: string;
  expiresInSeconds: number;
}

/** `KBR-68` — fetch a short-lived link to a document's ORIGINAL uploaded file.
 *
 *  Imperative rather than a `useQuery` hook on purpose: the link is presigned
 *  and expires in 15 minutes, so minting it when a citation chip is RENDERED
 *  would hand the reader a dead URL by the time they click it, and would mint
 *  one per chip on every answer. It is fetched on the click that uses it.
 *
 *  Access is re-established by the server from the signed-in user's own token —
 *  the citation is a hint, never a grant. A member gets a link only to a `live`
 *  document; anything else is 403 for them and 200 for an administrator, the
 *  same rule that guards the document's extracted text. */
export function useKbDocumentSource(): (documentId: string) => Promise<KbDocumentSource> {
  const api = useApi();
  return async (documentId: string) =>
    api.wikiLLM.get<KbDocumentSource>(`/v1/documents/${documentId}/source-url`);
}

export function useKbQuery(): UseMutationResult<KbQueryResult, Error, KbQueryInput> {
  const api = useApi();
  return useMutation({
    mutationFn: ({ question, domain, topK, projectId }: KbQueryInput): Promise<KbQueryResult> =>
      api.wikiLLM.post<KbQueryResult>("/v1/query", {
        // `top_k` is sent ONLY when a caller asks for one. It used to default to
        // 5 here, which silently overrode the server's own default and meant
        // KBR-41's change from 5 to 10 never reached this product at all — the
        // measured improvement was real and unreachable. The server owns it.
        body: {
          question,
          domain,
          ...(topK != null ? { top_k: topK } : {}),
          ...(projectId ? { project_id: projectId } : {}),
        },
      }),
  });
}

// --- concepts (Phase 2, `KBR-57`): the recognised-concept catalog ---
export interface KbConceptSummary {
  id: string;
  name: string;
  kind: "concept" | "entity";
  definition: string | null;
  aliases: string[];
  claimCount: number;
  collectionNames: string[];
}
export interface KbConceptClaim {
  claimId: string;
  predicate: string;
  statement: string;
  value: string | null;
  unit: string | null;
  conditions: string | null;
  quote: string;
  confidence: number;
  collectionId: string;
  collectionName: string;
  collectionKind: "company" | "project";
  documentId: string;
  documentTitle: string;
  pageStart: number | null;
  pageEnd: number | null;
}
export interface KbConceptDetail {
  id: string;
  name: string;
  kind: "concept" | "entity";
  definition: string | null;
  aliases: string[];
  claims: KbConceptClaim[];
  pages: { id: string; title: string; collectionId: string; collectionName: string }[];
}

interface ConceptListDto {
  concepts: {
    id: string;
    name: string;
    kind: "concept" | "entity";
    definition: string | null;
    aliases: string[];
    claimCount: number;
    collectionNames: string[];
  }[];
  total: number;
}

export function useKbConcepts(
  query: string,
): UseQueryResult<{ concepts: KbConceptSummary[]; total: number }> {
  const api = useApi();
  return useQuery({
    queryKey: ["kb", "concepts", query],
    queryFn: async ({ signal }) => {
      const qs = query.trim() ? `?query=${encodeURIComponent(query.trim())}` : "";
      const res = await api.wikiLLM.get<ConceptListDto>(`/v1/concepts${qs}`, { signal });
      return { concepts: res.concepts, total: res.total };
    },
  });
}

interface ConceptDetailDto {
  id: string;
  name: string;
  kind: "concept" | "entity";
  definition: string | null;
  aliases: string[];
  claims: {
    claimId: string;
    predicate: string;
    statement: string;
    value: string | null;
    unit: string | null;
    conditions: string | null;
    quote: string;
    confidence: number;
    collectionId: string;
    collectionName: string;
    collectionKind: "company" | "project";
    documentId: string;
    documentTitle: string;
    pageStart: number | null;
    pageEnd: number | null;
  }[];
  pages: { id: string; title: string; collectionId: string; collectionName: string }[];
}

export function useKbConcept(conceptId: string | null): UseQueryResult<KbConceptDetail> {
  const api = useApi();
  return useQuery({
    queryKey: ["kb", "concept", conceptId],
    enabled: !!conceptId,
    queryFn: async ({ signal }): Promise<KbConceptDetail> =>
      api.wikiLLM.get<ConceptDetailDto>(`/v1/concepts/${conceptId}`, { signal }),
  });
}

// --- save a chat answer back to the KB as an "Analisi" wiki page (Query step 5) ---
export interface SaveAnalysisInput {
  question: string;
  answer: string;
  citationPageIds: string[];
  /** `KBR-72` — the source DOCUMENTS the answer cited, for citations that name a
   *  PDF and no synthesized page. Without these a saved «Analisi» kept the
   *  answer and lost half its links. */
  citationDocumentIds: string[];
}
export interface AnalysisPageCreated {
  id: string;
  title: string;
  slug: string;
  type: WikiPageType;
  chunkCount: number;
}
interface AnalysisPageCreatedDto {
  id: string;
  title: string;
  slug: string;
  type: WikiPageType;
  chunk_count: number;
}

/** Persist a chat answer as an embedded `analysis` page; refreshes the graph so
 * the new purple node + its [[ref]] edges appear. */
export function useSaveAnalysis(): UseMutationResult<
  AnalysisPageCreated,
  Error,
  SaveAnalysisInput
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      question,
      answer,
      citationPageIds,
      citationDocumentIds,
    }: SaveAnalysisInput): Promise<AnalysisPageCreated> => {
      const dto = await api.wikiLLM.post<AnalysisPageCreatedDto>("/v1/pages/analysis", {
        body: {
          question,
          answer,
          citation_page_ids: citationPageIds,
          citation_document_ids: citationDocumentIds,
        },
      });
      return {
        id: dto.id,
        title: dto.title,
        slug: dto.slug,
        type: dto.type,
        chunkCount: dto.chunk_count,
      };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kb", "graph"] });
    },
  });
}

// --- rename / delete a wiki page (left-rail actions) ---
export interface RenameKbPageInput {
  id: string;
  title: string;
}

/** Rename a wiki page (PATCH /v1/pages/{id}). Slug + edges stay stable. */
export function useRenameKbPage(): UseMutationResult<void, Error, RenameKbPageInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, title }: RenameKbPageInput): Promise<void> => {
      await api.wikiLLM.patch<WikiPageDetailDto>(`/v1/pages/${id}`, { body: { title } });
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ["kb", "graph"] });
      void qc.invalidateQueries({ queryKey: ["kb", "page", id] });
    },
  });
}

export interface SaveKbPageBodyInput {
  id: string;
  bodyMarkdown: string;
}

/** Save an edited page body (PATCH /v1/pages/{id} with body_markdown). For an
 * Analisi page the backend also re-embeds it so future queries reflect the edit. */
export function useSaveKbPageBody(): UseMutationResult<void, Error, SaveKbPageBodyInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, bodyMarkdown }: SaveKbPageBodyInput): Promise<void> => {
      await api.wikiLLM.patch<WikiPageDetailDto>(`/v1/pages/${id}`, {
        body: { body_markdown: bodyMarkdown },
      });
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ["kb", "page", id] });
      void qc.invalidateQueries({ queryKey: ["kb", "graph"] });
    },
  });
}

/** Delete a wiki page (DELETE /v1/pages/{id}). For an Analisi page this also
 * removes its embedded chunks from future queries (backend cascades). */
export function useDeleteKbPage(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<void> => api.wikiLLM.del<void>(`/v1/pages/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kb", "graph"] });
    },
  });
}

// --- reviews ---
export const BE_TO_FE_DECISION: Record<string, KbDecision> = {
  accept_new: "accept_new",
  keep_existing: "reject_new",
  merge: "merge",
  clarify: "clarification",
};
export const FE_TO_BE_DECISION: Record<KbDecision, string> = {
  accept_new: "accept_new",
  reject_new: "keep_existing",
  merge: "merge",
  clarification: "clarify",
};
const BE_STATUS_TO_DECISION: Record<string, KbDecision | undefined> = {
  accepted: "accept_new",
  rejected: "reject_new",
  merged: "merge",
  clarified: "clarification",
};

interface ReviewItemDto {
  id: string;
  new_document_id: string;
  new_document_title: string;
  domain: string | null;
  uploader_sub: string;
  ai_recommendation: string;
  ai_reasoning: string;
  confidence: number;
  new_snippet: string;
  conflicting_snippet: string;
  status: string;
  decided_by_sub: string | null;
  decided_at: string | null;
  created_at: string;
  /** `KBR-62`. Absent on an older service build → `unknown`, never `complete`. */
  new_document_scan?: DocumentSummaryDto["scan"];
  /** `HON-10`. Absent on an older service build → shown as neither mechanism. */
  method?: string | null;
}

function toReview(it: ReviewItemDto): KbReview {
  return {
    id: it.id,
    newDocId: it.new_document_id,
    existingDocId: null,
    domain: resolveDomainKey(it.domain),
    uploader: it.uploader_sub,
    uploadedAt: it.created_at.slice(0, 16).replace("T", " "),
    confidence: it.confidence,
    newSnippet: it.new_snippet,
    existingSnippet: it.conflicting_snippet || null,
    existingDocSource: null,
    newDocSource: it.new_document_title,
    aiReasoning: it.ai_reasoning,
    aiRecommendation: BE_TO_FE_DECISION[it.ai_recommendation] ?? "clarification",
    // Only the two values the server can write are carried through. An unknown
    // string is dropped to `null` rather than displayed: a label naming the
    // mechanism must never be able to invent one.
    method: it.method === "deterministic" || it.method === "ai" ? it.method : null,
    decidedAt: it.decided_at ? it.decided_at.slice(0, 16).replace("T", " ") : undefined,
    decidedBy: it.decided_by_sub ?? undefined,
    decision: BE_STATUS_TO_DECISION[it.status],
    newDocScan: toScanOutcome(it.new_document_scan),
  };
}

export function useKbReviews(opts?: {
  decided?: boolean;
  enabled?: boolean;
}): UseQueryResult<KbReview[]> {
  const api = useApi();
  const decided = opts?.decided ?? false;
  return useQuery({
    queryKey: ["kb", "reviews", decided ? "decided" : "pending"],
    enabled: opts?.enabled ?? true,
    retry: adminRetry,
    queryFn: async ({ signal }): Promise<KbReview[]> => {
      const res = await api.wikiLLM.get<{ items: ReviewItemDto[]; total: number }>("/v1/reviews", {
        signal,
        params: { decided },
      });
      return res.items.map(toReview);
    },
  });
}

export interface DecideReviewInput {
  id: string;
  decision: KbDecision;
  note?: string;
}

export function useDecideReview(): UseMutationResult<void, Error, DecideReviewInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, note }: DecideReviewInput): Promise<void> =>
      api.wikiLLM.post<void>(`/v1/reviews/${id}/decide`, {
        body: { decision: FE_TO_BE_DECISION[decision], note },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kb", "reviews"] });
      void qc.invalidateQueries({ queryKey: ["kb", "documents"] });
      void qc.invalidateQueries({ queryKey: ["kb", "graph"] });
    },
  });
}

// --- lint ---
interface LintFindingDto {
  kind: string;
  target_type: string;
  target_id: string;
  detail: string;
  linked_review_id: string | null;
}
export const LINT_KIND_TO_CATEGORY: Record<string, LintCategory> = {
  orphan_no_chunks: "orphan",
  orphan: "orphan",
  contradiction: "contradiction",
  stale: "stale",
  missing_cross_reference: "missing-link",
};

function toLintFinding(f: LintFindingDto): KbLintFinding {
  const category = LINT_KIND_TO_CATEGORY[f.kind] ?? "data-gap";
  return {
    id: `${f.kind}:${f.target_id}`,
    category,
    severity: category === "contradiction" || category === "stale" ? "warn" : "info",
    title: f.kind.replace(/_/g, " "),
    summary: f.detail,
    suggestion: "",
    pageA: ["page", "document"].includes(f.target_type) ? f.target_id : undefined,
    linkedReviewId: f.linked_review_id ?? undefined,
  };
}

export function useKbLint(opts?: { enabled?: boolean }): UseQueryResult<KbLintFinding[]> {
  const api = useApi();
  return useQuery({
    queryKey: ["kb", "lint"],
    enabled: opts?.enabled ?? true,
    retry: adminRetry,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<KbLintFinding[]> => {
      const res = await api.wikiLLM.post<{ findings: LintFindingDto[] }>("/v1/lint", {});
      return res.findings.map(toLintFinding);
    },
  });
}
